<template>
	<section id="schedule" class="container timetable">
		<div class="head">
			<div class="section-head">
				<span class="label">Timetable</span>
				<h2 class="cap section-title">The week</h2>
			</div>
			<div class="pill-group" role="group" aria-label="Show classes for">
				<button
					v-for="option in audiences"
					:key="option.id"
					type="button"
					class="pill"
					:aria-pressed="audience === option.id"
					@click="audience = option.id"
				>
					{{ option.label }}
				</button>
			</div>
		</div>

		<!-- Desktop / tablet: the whole week as columns -->
		<div class="card week-card">
			<div class="week">
				<div v-for="(classes, i) in week" :key="days[i]" class="day-col">
					<h3 class="cap day-name">{{ shortDays[i] }}</h3>
					<div v-for="item in classes" :key="item.time + item.name" :class="['slot', item.kind]">
						<span class="slot-time">{{ item.time }}</span>
						<span class="slot-name">{{ item.name }}</span>
					</div>
				</div>
			</div>
		</div>

		<!-- Phones: pick a day, see that day's classes -->
		<div class="phone">
			<div class="day-tabs" role="group" aria-label="Day">
				<button
					v-for="(name, i) in shortDays"
					:key="name"
					type="button"
					class="day-tab"
					:aria-pressed="day === i"
					:aria-label="days[i]"
					@click="day = i"
				>
					{{ name }}
				</button>
			</div>
			<div class="card phone-card">
				<h3 class="cap day-name">{{ days[day] }}</h3>
				<div v-for="item in week[day]" :key="item.time + item.name" :class="['slot', 'slot-row', item.kind]">
					<span class="slot-time">{{ item.time }}</span>
					<span class="slot-name">{{ item.name }}</span>
				</div>
			</div>
		</div>

		<div class="legend">
			<span v-for="item in legend" :key="item.label"><i :class="item.kind"></i>{{ item.label }}</span>
		</div>
	</section>
</template>

<script>
import { days, schedule } from "../../data/site.js";

export default {
	data() {
		// Open on today's weekday (Sunday falls back to Monday)
		const today = new Date().getDay();
		return {
			days,
			shortDays: days.map((d) => d.slice(0, 3)),
			audience: "adults",
			day: today === 0 ? 0 : today - 1,
			audiences: [
				{ id: "adults", label: "Adults" },
				{ id: "kids", label: "Kids" },
			],
		};
	},
	computed: {
		week() {
			return schedule[this.audience];
		},
		legend() {
			if (this.audience === "kids") {
				return [{ kind: "kids", label: "Kids classes by age group" }];
			}
			return [
				{ kind: "gi", label: "BJJ (Gi)" },
				{ kind: "nogi", label: "NoGi" },
				{ kind: "women", label: "Women's only" },
			];
		},
	},
};
</script>

<style scoped>
.timetable {
	padding-top: 80px;
	padding-bottom: 80px;
	display: flex;
	flex-direction: column;
	gap: 32px;
}

.head {
	display: flex;
	flex-wrap: wrap;
	justify-content: space-between;
	align-items: flex-end;
	gap: 20px;
}

.week-card {
	padding: 20px;
	overflow-x: auto;
}

.week {
	min-width: 920px;
	display: grid;
	grid-template-columns: repeat(6, minmax(0, 1fr));
	gap: 10px;
}

.day-col {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.day-name {
	padding-bottom: 8px;
	border-bottom: 2px solid var(--ink);
	font-size: 24px;
}

.slot {
	display: flex;
	flex-direction: column;
	padding: 12px 14px;
	border-radius: 10px;
}

.slot-time {
	font-weight: 700;
	font-size: 15px;
}

.slot-name {
	font-size: 13px;
}

.gi {
	background: var(--mat);
	color: var(--ink);
}

.nogi {
	background: var(--white);
	color: var(--ink);
	box-shadow: inset 0 0 0 1.5px var(--ink);
}

.women {
	background: var(--ink);
	color: var(--white);
}

.kids {
	background: var(--mat-tint);
	color: var(--ink);
}

.legend {
	display: flex;
	flex-wrap: wrap;
	gap: 20px;
	font-size: 13px;
	color: #444444;
}

.legend span {
	display: flex;
	align-items: center;
	gap: 8px;
}

.legend i {
	width: 14px;
	height: 14px;
	border-radius: 4px;
}

.phone {
	display: none;
}

@media (max-width: 768px) {
	.timetable {
		padding-top: 48px;
		padding-bottom: 0;
		gap: 16px;
	}

	.head {
		flex-direction: column;
		align-items: stretch;
		gap: 16px;
	}

	.pill-group {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}

	.pill {
		min-height: 44px;
		font-size: 15px;
	}

	.week-card {
		display: none;
	}

	.phone {
		display: flex;
		flex-direction: column;
		gap: 16px;
	}

	.day-tabs {
		display: grid;
		grid-template-columns: repeat(6, minmax(0, 1fr));
		gap: 6px;
	}

	.day-tab {
		min-height: 48px;
		padding: 0;
		border: 1px solid var(--line);
		border-radius: 10px;
		background: var(--white);
		color: var(--ink);
		font: 600 14px var(--body);
		cursor: pointer;
	}

	.day-tab[aria-pressed="true"] {
		border-color: var(--ink);
		background: var(--ink);
		color: var(--white);
	}

	.phone-card {
		padding: 14px;
		display: flex;
		flex-direction: column;
		gap: 8px;
	}

	.phone-card .day-name {
		font-size: 26px;
	}

	.slot-row {
		min-height: 56px;
		flex-direction: row;
		align-items: center;
		gap: 12px;
		padding: 14px 16px;
	}

	.slot-row .slot-time {
		width: 62px;
		flex-shrink: 0;
		font-size: 17px;
	}

	.slot-row .slot-name {
		font-size: 15px;
	}

	.legend {
		gap: 14px;
		font-size: 12px;
	}

	.legend i {
		width: 12px;
		height: 12px;
		border-radius: 3px;
	}
}
</style>
